import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { format, parseISO, addDays, subDays, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { HorarioDisponible } from '../types';
import { obtenerHorariosDisponibles } from '../services/googleCalendarService';

const Container = styled.div`
  margin: 20px 0;
`;

const Title = styled.h2`
  color: #0078d4;
  font-size: 1.8rem;
  margin-bottom: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 15px;
  }
`;

const ControlesSemana = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
  
  @media (max-width: 400px) {
    margin-bottom: 20px;
    gap: 10px;
  }
`;

const BotonSemana = styled.button`
  background-color: #0078d4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 15px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 3px 6px rgba(0, 120, 212, 0.2);

  &:hover {
    background-color: #005a9e;
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 10px;
  }
  
  @media (max-width: 400px) {
    padding: 8px;
    font-size: 0.9rem;
  }
`;

const SemanaInfo = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    order: -1;
    font-size: 1rem;
  }
  
  @media (max-width: 400px) {
    font-size: 0.9rem;
    padding: 6px 10px;
  }
`;

const CalendarIcon = styled.span`
  color: #0078d4;
  font-size: 1.3rem;
`;

const GridDias = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const DiaTarjeta = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DiaCabecera = styled.div`
  background-color: #0078d4;
  color: white;
  padding: 10px 8px;
  text-align: center;
  font-weight: bold;
  font-size: 0.92rem;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 8px 6px;
  }
  
  @media (max-width: 400px) {
    font-size: 0.8rem;
    padding: 7px 5px;
  }
`;

const HorasContenedor = styled.div`
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 480px) {
    padding: 8px 6px;
    gap: 6px;
  }
  
  @media (max-width: 400px) {
    padding: 6px 5px;
  }
`;

const HoraBoton = styled.button<{ selected: boolean }>`
  background-color: ${props => props.selected ? '#0078d4' : '#ffffff'};
  color: ${props => props.selected ? '#ffffff' : '#333333'};
  border: 1px solid ${props => props.selected ? '#0078d4' : '#dddddd'};
  border-radius: 6px;
  padding: 8px 6px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  font-weight: ${props => props.selected ? '500' : 'normal'};
  font-size: 0.95rem;
  box-shadow: ${props => props.selected ? '0 2px 6px rgba(0, 120, 212, 0.3)' : 'none'};
  
  &:hover {
    background-color: ${props => props.selected ? '#005a9e' : '#f0f0f0'};
    border-color: ${props => props.selected ? '#005a9e' : '#0078d4'};
    transform: translateY(-2px);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: ${props => props.selected ? '4px' : '0'};
    height: 100%;
    background-color: #005a9e;
    left: 0;
    top: 0;
    transition: width 0.2s ease;
  }
  
  @media (max-width: 480px) {
    padding: 7px 5px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 400px) {
    padding: 7px 4px;
    font-size: 0.85rem;
  }
`;

const MensajeNoDisponible = styled.p`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 15px 10px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 20px;
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 8px;
  margin: 20px 0;
`;

const NoDisponibilidadContainer = styled.div`
  text-align: center;
  padding: 25px;
  color: #ff6d00;
  background-color: #fff3e0;
  border-radius: 8px;
  margin: 20px 0;
  font-size: 1.1rem;
  font-weight: 500;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const IconoAlerta = styled.span`
  font-size: 2rem;
  margin-bottom: 5px;
`;

const BotonAccion = styled.button`
  background-color: #ff6d00;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  margin-top: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e65100;
    transform: translateY(-2px);
  }
`;

interface HorarioSelectorProps {
  fisioterapeutaId: string;
  onSelectHorario: (fecha: string, hora: string) => void;
  fechaSeleccionada?: string;
  horaSeleccionada?: string;
  nombreFisioterapeuta?: string;
}

const HorarioSelector: React.FC<HorarioSelectorProps> = ({
  fisioterapeutaId,
  onSelectHorario,
  fechaSeleccionada,
  horaSeleccionada,
  nombreFisioterapeuta = 'el fisioterapeuta' // Valor por defecto si no se proporciona el nombre
}) => {
  const [fechaInicio, setFechaInicio] = useState<Date>(new Date());
  const [horarios, setHorarios] = useState<HorarioDisponible[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fisioterapeutaId) return;

    const cargarHorarios = async () => {
      try {
        setLoading(true);
        
        // Mostrar información de depuración sobre zonas horarias
        console.log('Fecha y zona horaria del navegador:', new Date().toString());
        console.log('Timezone del navegador:', Intl.DateTimeFormat().resolvedOptions().timeZone);
        console.log('Fecha formateada para API:', format(fechaInicio, 'yyyy-MM-dd'));
        
        const data = await obtenerHorariosDisponibles(fisioterapeutaId, fechaInicio);
        console.log('Horarios recibidos del servidor:', data);
        
        // Log detallado de cada fecha y sus horas disponibles
        if (data && data.length > 0) {
          console.log('Detalle de horarios disponibles:');
          data.forEach(dia => {
            console.log(`Fecha: ${dia.fecha}`);
            console.log(`Horas disponibles (${dia.horasDisponibles.length}): ${dia.horasDisponibles.join(', ')}`);
          });
          
          // Usar los datos directamente sin ajuste manual
          // El backend ya maneja correctamente la zona horaria con dayjs
          setHorarios(data);
        } else {
          setHorarios(data);
        }
        
        setError(null);
      } catch (err) {
        setError('Error al cargar los horarios disponibles. Por favor, inténtalo de nuevo más tarde.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    cargarHorarios();
  }, [fisioterapeutaId, fechaInicio]);

  const avanzarSemana = () => {
    setFechaInicio(prevFecha => addDays(prevFecha, 7));
  };

  const retrocederSemana = () => {
    const nuevaFecha = subDays(fechaInicio, 7);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    // No permitir seleccionar semanas pasadas
    if (nuevaFecha >= hoy) {
      setFechaInicio(nuevaFecha);
    }
  };

  const esFechaActual = (fecha: string): boolean => {
    return fecha === fechaSeleccionada;
  };

  const esHoraActual = (fecha: string, hora: string): boolean => {
    return fecha === fechaSeleccionada && hora === horaSeleccionada;
  };

  const formatearFecha = (fecha: string): string => {
    try {
      const fechaObj = parseISO(fecha);
      if (!isValid(fechaObj)) throw new Error('Fecha inválida');
      
      // Formato más compacto para móviles
      const formatStr = window.innerWidth <= 400 ? 'EEE d MMM' : 'EEEE d MMM';
      return format(fechaObj, formatStr, { locale: es });
    } catch (error) {
      console.error('Error al formatear la fecha:', error);
      return fecha;
    }
  };

  const fechaFinSemana = addDays(fechaInicio, 6);
  const textoSemana = `${format(fechaInicio, 'dd/MM/yyyy')} - ${format(fechaFinSemana, 'dd/MM/yyyy')}`;

  if (loading) {
    return <LoadingContainer>Cargando horarios disponibles...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  // Verificar si hay días con horas disponibles en esta semana
  const hayDisponibilidad = horarios.some(dia => dia.horasDisponibles.length > 0);

  // Si no hay disponibilidad, mostrar mensaje específico
  if (!hayDisponibilidad && horarios.length > 0) {
    return (
      <Container>
        <Title>Selecciona un Horario</Title>
        <ControlesSemana>
          <BotonSemana onClick={retrocederSemana}>
            <span>←</span> Anterior
          </BotonSemana>
          <SemanaInfo>
            <CalendarIcon>📅</CalendarIcon> {textoSemana}
          </SemanaInfo>
          <BotonSemana onClick={avanzarSemana}>
            Siguiente <span>→</span>
          </BotonSemana>
        </ControlesSemana>
        
        <NoDisponibilidadContainer>
          <IconoAlerta>⚠️</IconoAlerta>
          <div>
            No hay horas disponibles para <strong>{nombreFisioterapeuta}</strong> en esta semana.
          </div>
          <div>
            Por favor, selecciona otra semana o consulta la disponibilidad de otro profesional.
          </div>
          <BotonAccion onClick={avanzarSemana}>
            Ver semana siguiente <span>→</span>
          </BotonAccion>
        </NoDisponibilidadContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Selecciona un Horario</Title>
      <ControlesSemana>
        <BotonSemana onClick={retrocederSemana}>
          <span>←</span> Anterior
        </BotonSemana>
        <SemanaInfo>
          <CalendarIcon>📅</CalendarIcon> {textoSemana}
        </SemanaInfo>
        <BotonSemana onClick={avanzarSemana}>
          Siguiente <span>→</span>
        </BotonSemana>
      </ControlesSemana>

      <GridDias>
        {horarios.map((dia) => (
          <DiaTarjeta key={dia.fecha}>
            <DiaCabecera>{formatearFecha(dia.fecha)}</DiaCabecera>
            <HorasContenedor>
              {dia.horasDisponibles.length > 0 ? (
                dia.horasDisponibles.map((hora) => (
                  <HoraBoton
                    key={hora}
                    onClick={() => onSelectHorario(dia.fecha, hora)}
                    selected={esHoraActual(dia.fecha, hora)}
                    title={`Cita a las ${hora} (hora local de España).
Esta es la hora real a la que tendrá lugar la cita.`}
                  >
                    {hora}
                  </HoraBoton>
                ))
              ) : (
                <MensajeNoDisponible>No hay horas disponibles</MensajeNoDisponible>
              )}
            </HorasContenedor>
          </DiaTarjeta>
        ))}
      </GridDias>
    </Container>
  );
};

export default HorarioSelector; 
